import { useLoaderData, LoaderFunctionArgs, useNavigate } from "react-router-dom";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { GET_COMMISSARY_ORDER } from "./queries";
import { UPDATE_COMMISSARY_ORDER_STATUS } from "./mutations";
import { CommissaryOrder } from "../../types/order-commissary";
import { useState } from "react";
import moment from "moment/moment";
import toast from "react-hot-toast";
import { usdFormat } from "../../utils";
import { SecondaryButton } from "../../components/Button";

const client = createApolloClient();

export const commissaryOrderShowLoader = async ({
  params,
}: LoaderFunctionArgs) => {
  const { id } = params;
  const { data } = await client.query({
    query: GET_COMMISSARY_ORDER,
    variables: { id: parseInt(id!, 10) },
  });
  return data.order_commissary_by_pk;
};

const statusBadgeColor = (status: string) => {
  switch (status) {
    case "pending":
      return "bg-yellow-100 text-yellow-800";
    case "confirmed":
      return "bg-blue-100 text-blue-800";
    case "in_progress":
      return "bg-indigo-100 text-indigo-800";
    case "ready":
      return "bg-sky-100 text-sky-800";
    case "fulfilled":
      return "bg-green-100 text-green-800";
    case "rejected":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const formatStatus = (status: string) =>
  status
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

export const CommissaryOrderShow = () => {
  const order = useLoaderData() as CommissaryOrder;
  const navigate = useNavigate();
  const [currentOrder, setCurrentOrder] = useState<CommissaryOrder>(order);
  const [organizerNotes, setOrganizerNotes] = useState(
    order.organizer_notes ?? ""
  );
  const [selectedStatus, setSelectedStatus] = useState(order.status);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  const hasChanges =
    selectedStatus !== currentOrder.status ||
    organizerNotes !== (currentOrder.organizer_notes ?? "");

  const handleStatusUpdate = async (
    nextStatus: string,
    reason?: string
  ) => {
    setIsUpdating(true);
    try {
      const variables: any = {
        id: currentOrder.id,
        status: nextStatus,
        organizer_notes: organizerNotes || null,
        updated_at: new Date().toISOString(),
      };

      if (nextStatus === "rejected") {
        variables.rejection_reason = reason || null;
      }

      if (nextStatus === "fulfilled") {
        variables.fulfilled_at = new Date().toISOString();
      }

      await client.mutate({
        mutation: UPDATE_COMMISSARY_ORDER_STATUS,
        variables,
      });

      setCurrentOrder((prev) => ({
        ...prev,
        status: nextStatus,
        organizer_notes: organizerNotes,
        rejection_reason: nextStatus === "rejected" ? reason : prev.rejection_reason,
        fulfilled_at:
          nextStatus === "fulfilled"
            ? new Date().toISOString()
            : prev.fulfilled_at,
      }));

      setSelectedStatus(nextStatus);
      toast.success(`Order status updated to ${formatStatus(nextStatus)}`);
      setShowRejectModal(false);
      setRejectionReason("");
    } catch (error) {
      toast.error("Failed to update order status: " + error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto py-6 px-4">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-gray-900">
            Commissary Order #{currentOrder.id}
          </h1>
          <span
            className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${statusBadgeColor(
              currentOrder.status
            )}`}
          >
            {formatStatus(currentOrder.status)}
          </span>
        </div>
        <SecondaryButton onClick={() => navigate("/commissary-orders/list")}>
          Back to List
        </SecondaryButton>
      </div>

      {/* Order Info */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Order Details</h2>
        </div>
        <div className="px-6 py-4 grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-500">Vendor</p>
            <p className="text-sm text-gray-900">
              {currentOrder.vendor?.name ?? "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Vendor Contact</p>
            <p className="text-sm text-gray-900">
              {currentOrder.vendor?.poc_name ?? "N/A"}
              {currentOrder.vendor?.poc_phone &&
                ` - ${currentOrder.vendor.poc_phone}`}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Placed By</p>
            <p className="text-sm text-gray-900">
              {currentOrder.user
                ? `${currentOrder.user.first_name ?? ""} ${
                    currentOrder.user.last_name ?? ""
                  }`.trim() || "N/A"
                : "N/A"}
              {currentOrder.user?.phone_number &&
                ` - ${currentOrder.user.phone_number}`}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">SMS Notify</p>
            <p className="text-sm text-gray-900">
              {currentOrder.sms_notify ? "Yes" : "No"}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Created</p>
            <p className="text-sm text-gray-900">
              {moment(currentOrder.created_at).format(
                "MMM DD, YYYY h:mm a"
              )}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Last Updated</p>
            <p className="text-sm text-gray-900">
              {moment(currentOrder.updated_at).format(
                "MMM DD, YYYY h:mm a"
              )}
            </p>
          </div>
          {currentOrder.fulfilled_at && (
            <div>
              <p className="text-sm font-medium text-gray-500">Fulfilled At</p>
              <p className="text-sm text-gray-900">
                {moment(currentOrder.fulfilled_at).format(
                  "MMM DD, YYYY h:mm a"
                )}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Line Items */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Items</h2>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Item
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Qty
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Unit Price
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Total
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Notes
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {(currentOrder.items ?? []).map((item, idx) => (
              <tr key={idx}>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {item.item_name}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 text-right">
                  {item.quantity}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 text-right">
                  {usdFormat.format(item.unit_price / 100)}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 text-right">
                  {usdFormat.format(item.total_price / 100)}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {item.notes ?? "-"}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50">
            <tr>
              <td
                colSpan={3}
                className="px-6 py-3 text-sm font-medium text-gray-900 text-right"
              >
                Subtotal
              </td>
              <td className="px-6 py-3 text-sm font-medium text-gray-900 text-right">
                {usdFormat.format(currentOrder.subtotal / 100)}
              </td>
              <td />
            </tr>
            {currentOrder.tax > 0 && (
              <tr>
                <td
                  colSpan={3}
                  className="px-6 py-3 text-sm font-medium text-gray-900 text-right"
                >
                  Tax
                </td>
                <td className="px-6 py-3 text-sm font-medium text-gray-900 text-right">
                  {usdFormat.format(currentOrder.tax / 100)}
                </td>
                <td />
              </tr>
            )}
            <tr>
              <td
                colSpan={3}
                className="px-6 py-3 text-sm font-bold text-gray-900 text-right"
              >
                Total
              </td>
              <td className="px-6 py-3 text-sm font-bold text-gray-900 text-right">
                {usdFormat.format(currentOrder.total / 100)}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Notes Section */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Notes</h2>
        </div>
        <div className="px-6 py-4 space-y-4">
          {currentOrder.notes && (
            <div>
              <p className="text-sm font-medium text-gray-500">Vendor Notes</p>
              <p className="text-sm text-gray-900 mt-1">
                {currentOrder.notes}
              </p>
            </div>
          )}

          {currentOrder.rejection_reason && (
            <div>
              <p className="text-sm font-medium text-red-500">
                Rejection Reason
              </p>
              <p className="text-sm text-gray-900 mt-1">
                {currentOrder.rejection_reason}
              </p>
            </div>
          )}

          <div>
            <label
              htmlFor="organizer-notes"
              className="block text-sm font-medium text-gray-500"
            >
              Organizer Notes
            </label>
            <textarea
              id="organizer-notes"
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              value={organizerNotes}
              onChange={(e) => setOrganizerNotes(e.target.value)}
              placeholder="Add notes for this order..."
            />
          </div>
        </div>
      </div>

      {/* Status Selection */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Update Status</h2>
        </div>
        <div className="px-6 py-4 flex items-center gap-3 flex-wrap">
          {["pending", "confirmed", "in_progress", "ready", "fulfilled", "rejected"].map(
            (s) => (
              <button
                key={s}
                onClick={() =>
                  s === "rejected"
                    ? setShowRejectModal(true)
                    : setSelectedStatus(s)
                }
                className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold shadow-sm transition-all ${
                  selectedStatus === s
                    ? "ring-2 ring-offset-2 ring-primary-500"
                    : ""
                } ${
                  s === "pending"
                    ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                    : s === "confirmed"
                    ? "bg-blue-100 text-blue-800 hover:bg-blue-200"
                    : s === "in_progress"
                    ? "bg-indigo-100 text-indigo-800 hover:bg-indigo-200"
                    : s === "ready"
                    ? "bg-sky-100 text-sky-800 hover:bg-sky-200"
                    : s === "fulfilled"
                    ? "bg-green-100 text-green-800 hover:bg-green-200"
                    : "bg-red-100 text-red-800 hover:bg-red-200"
                }`}
              >
                {formatStatus(s)}
              </button>
            )
          )}
        </div>
      </div>

      {/* Save / Cancel Footer */}
      <div className="flex justify-end gap-3">
        <button
          onClick={() => navigate(-1)}
          type="button"
          className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          style={{ lineHeight: 2 }}
        >
          Cancel
        </button>
        <button
          onClick={() => handleStatusUpdate(selectedStatus)}
          disabled={isUpdating || !hasChanges}
          className="inline-flex justify-center items-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
          style={{ lineHeight: 2 }}
        >
          {isUpdating ? "Saving..." : "Save"}
        </button>
      </div>

      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Reject Order
              </h3>
            </div>
            <div className="px-6 py-4">
              <label
                htmlFor="rejection-reason"
                className="block text-sm font-medium text-gray-700"
              >
                Reason for rejection
              </label>
              <textarea
                id="rejection-reason"
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter reason for rejecting this order..."
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <SecondaryButton
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason("");
                }}
              >
                Cancel
              </SecondaryButton>
              <button
                onClick={() =>
                  handleStatusUpdate("rejected", rejectionReason)
                }
                disabled={isUpdating || !rejectionReason.trim()}
                className="inline-flex items-center rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-50"
              >
                Reject Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
